import React, { createContext, useState, useContext, useEffect, useRef } from "react";
import io from "socket.io-client";
import { SOCKET_URL, DOMAIN_URL } from "../../config";
import { useSocket } from "../../components/SocketConnection";
import { getUserData } from "../../Actions/common";

const NotificationContext = createContext();

export const NotificationProvider = ({ children }) => {
    const [notifications, setNotifications] = useState([]);
    const [displayedNotifications, setDisplayedNotifications] = useState([]);
    const [userId, setUserId] = useState(null);
    const [anchorElement, setAnchorElement] = useState(null);
    const isMounted = useRef(false); // Track initial render
    // const socketRef = useRef(null);
    const chunkSize = 5;
    const { socketConnection, setSocketConnectionData } = useSocket();

    const unseenNotification = notifications?.filter(
        notification => !notification?.isRead
    ).length;

    // Initialize socket connection
    useEffect(() => {
        if (!socketConnection && !isMounted.current) {
            isMounted.current = true;

            const newConnection = io(SOCKET_URL, {
                reconnection: true,
                reconnectionDelay: 1000,
            })
            newConnection.on("connect",()=>{
                const userData = localStorage.getItem("userData") ? getUserData() : null;
                newConnection.emit("join", userData?.id, DOMAIN_URL);
            })
            setSocketConnectionData(newConnection);
        }

        return () => {
            if (socketConnection) {
                socketConnection.disconnect();
                setSocketConnectionData(null);
            }
        };
    }, [socketConnection,setSocketConnectionData]);

    const addNotifications = (newNotifications, userId) => {
        setUserId(userId);

        setNotifications((prevNotifications) => {
            const incoming = newNotifications.filter(
                (n) => Number(n.toUser) === userId
            );
            // Dedup: merge incoming with existing, incoming wins on same ID
            const existingMap = new Map(prevNotifications.map((n) => [n.id, n]));
            incoming.forEach((n) => existingMap.set(n.id, n));
            return Array.from(existingMap.values()).sort((a, b) =>
                a.isRead === b.isRead ? 0 : a.isRead ? 1 : -1
            );
        });

        setDisplayedNotifications((prev) => {
            const incoming = newNotifications.filter(
                (n) => Number(n.toUser) === userId
            );
            const existingMap = new Map(prev.map((n) => [n.id, n]));
            incoming.forEach((n) => existingMap.set(n.id, n));
            return Array.from(existingMap.values())
                .sort((a, b) => (a.isRead === b.isRead ? 0 : a.isRead ? 1 : -1))
                .slice(0, chunkSize);
        });
    };

    const handleNotificationClick = (event) => {
        if (!userId) {
            console.error("User ID is undefined");
            return;
        }

        const unreadNotifications = notifications.filter(
            notification => !notification.isRead
        );

        if (unreadNotifications.length > 0) {
            const firstFiveIds = unreadNotifications
                .slice(0, chunkSize)
                .map(notification => notification.id);

            socketConnection.emit("markNotificationsRead", {
                userId,
                notificationIds: firstFiveIds,
            });

            setNotifications(prev =>
                prev.map(notif =>
                    firstFiveIds.includes(notif.id)
                        ? { ...notif, isRead: true }
                        : notif
                )
            );
        }
        setAnchorElement(event.currentTarget);
    };

    const loadMoreNotifications = () => {
        const nextChunk = notifications.slice(
            displayedNotifications.length,
            displayedNotifications.length + chunkSize
        );

        if (nextChunk.length > 0) {
            const nextChunkIds = nextChunk.map(notif => notif.id);
            
            socketConnection.emit("markNotificationsRead", {
                userId,
                notificationIds: nextChunkIds,
            });

            setNotifications(prev =>
                prev.map(notif =>
                    nextChunkIds.includes(notif.id)
                        ? { ...notif, isRead: true }
                        : notif
                )
            );

            setDisplayedNotifications(prev => [...prev, ...nextChunk]);
        }
    };

    const handleCloseNotification = () => {
        setDisplayedNotifications(notifications.slice(0, chunkSize));
        setAnchorElement(null);
    };

    return (
        <NotificationContext.Provider
            value={{
                notifications,
                loadMoreNotifications,
                addNotifications,
                handleNotificationClick,
                anchorElement,
                handleCloseNotification,
                displayedNotifications,
                unseenNotification,
            }}
        >
            {children}
        </NotificationContext.Provider>
    );
};

export const useNotification = () => useContext(NotificationContext);
